/* eslint-disable no-underscore-dangle */
/* eslint-disable @stencil-community/required-jsdoc */
import { Component, Host, h, Prop, State, Listen, getAssetPath } from '@stencil/core';
import { Memoize, isEmpty, parseIfString } from '@springbokagency/stencil-base';

import checkWindowSize from '../../../utils/checkWindowSize';
import pageTracking from '../../../utils/pageTracking';
import type { VideoModal, ModalVideoGroupItem, VideoItem } from './video-modal.types';

import state from '../../../store/store';
import { deepQuerySelector } from '../../../utils/deepQuerySelector';

@Component({
  tag: 'efb-video-modal',
  styleUrl: 'video-modal.scss',
  shadow: true,
})
export class EfbVideoModal {
  private modalRef: HTMLEfbBaseModalElement;

  private videoPlayerSeekerRef: HTMLInputElement;

  private needSlider: boolean;

  private existingMetaDescription: string;

  private existingMetaImage: string;

  /**
   * State
   */

  @State() isMobile: boolean = checkWindowSize().isMobile;

  @State() isSharingModalOpen: boolean = false;

  @State() activeGroupData: ModalVideoGroupItem;

  @State() activeVideoIndex: number = 0;

  @State() activeVideoTitle: string;

  @State() currentActiveVideo: VideoItem;

  @State() videoIsReady: boolean = false;

  @State() currentVideoDuration: number;

  @State() videoIsPlaying: boolean = true;

  @State() videoIsMuted: boolean = false;

  @State() playVideo: boolean = true;

  @State() seekTo: number;

  @State() externalLinkIsVisible: boolean = false;

  @State() modalInitTrackedPageId: number;

  /**
   * Props
   */

  @Prop() modalData: VideoModal | string;

  @Memoize('modalData')
  get parsedModalData(): VideoModal {
    return parseIfString(this.modalData);
  }

  @Prop() videoGroups: ModalVideoGroupItem[] | string;

  @Memoize('videoGroups')
  get parsedVideoGroups(): ModalVideoGroupItem[] {
    return parseIfString(this.videoGroups);
  }

  @Prop() activeVideoGroupId?: string;

  @Prop() activeVideoId?: string;

  /**
   * Events
   */

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    this.isMobile = checkWindowSize().isMobile;
  }

  @Listen('videoIsReady')
  videoIsReadyHandler() {
    this.videoIsReady = true;
  }

  @Listen('setCurrentVideoDuration')
  setCurrentVideoDurationHandler(e: CustomEvent) {
    this.currentVideoDuration = e.detail;
  }

  @Listen('videoIsPlaying')
  videoIsPlayingHandler(e: CustomEvent) {
    this.videoIsPlaying = e.detail;
    this.playVideo = e.detail;
  }

  @Listen('closeModal')
  closeModalHandler(e: CustomEvent) {
    const { modalId } = e.detail;

    if (modalId === 'sharing-modal') {
      this.isSharingModalOpen = false;
    }
  }

  @Listen('videoTimeUpdated')
  videoTimeUpdatedHandler(e: CustomEvent) {
    this.videoPlayerSeekerRef.value = (e.detail * 100).toString();
  }

  @Listen('hasEnteredLastPortion')
  hasEnteredLastPortionHandler() {
    this.externalLinkIsVisible = true;
  }

  @Listen('videoSliderSlideWillMove')
  videoSliderSlideWillMoveHandler() {
    this.externalLinkIsVisible = false;
  }

  @Listen('videoSliderSlideChanged')
  videoSliderSlideChangedHandler(e: CustomEvent) {
    // define current video
    this.currentActiveVideo = this.activeGroupData.videos[e.detail.index];
    this.activeVideoTitle = this.currentActiveVideo.title;

    const trackingId = this.currentActiveVideo.id;

    // store active item's trackind id
    state.activeVideoTrackingId = trackingId;

    // trigger manually page tracking
    if (this.modalInitTrackedPageId !== e.detail.index) {
      pageTracking(
        state.currentLanguage,
        `${this.activeGroupData?.dataAnalyticsId}${!isEmpty(trackingId) ? `-${trackingId}` : ''}`
      );
    }

    // change meta tags
    this.changeMetaTags(this.activeGroupData.videos[e.detail.index]);
  }

  /**
   * Methods
   */

  private handleCloseClick() {
    this.modalRef.close();
  }

  private clickPlayVideo() {
    if (this.videoIsPlaying) {
      this.playVideo = false;
    } else {
      this.playVideo = true;
    }
  }

  private async handleShareClick() {
    let imageFile: File;

    if (!isEmpty(this.currentActiveVideo.metaImage)) {
      // Convert dataUrl into blob using browser fetch API
      const imageUrl = getAssetPath(
        `/dist/assets/images/sharing/${this.currentActiveVideo.metaImage}-${state.currentLanguage}.jpg`
      );

      const blob = await (await fetch(imageUrl)).blob();

      // Create file form the blob
      imageFile = new File([blob], `${this.currentActiveVideo.metaImage}.jpg`, {
        type: blob.type,
      });
    }

    if (
      /Mobi|Android/i.test(navigator.userAgent) &&
      typeof navigator.share === 'function' &&
      navigator.canShare &&
      navigator.canShare({ files: [imageFile] }) &&
      !isEmpty(this.currentActiveVideo.metaImage)
    ) {
      try {
        navigator.share({
          files: [imageFile],
        });
      } catch (error) {
        console.log('Sharing failed!', error);
      }
    } else {
      this.isSharingModalOpen = !this.isSharingModalOpen;
      console.log('This device does not support sharing files.');
    }
  }

  private handleMuteClick() {
    this.videoIsMuted = !this.videoIsMuted;
    sessionStorage.setItem('muteVideos', `${this.videoIsMuted}`);
  }

  private renderVideoGroup() {
    if (isEmpty(this.videoGroups)) return null;

    // define active group & video item data depending on url params
    // (useful for sharing feature)

    this.activeGroupData = this.parsedVideoGroups.find(
      (group) => group.modalGroupId === this.activeVideoGroupId
    );

    this.needSlider = this.activeGroupData.videos.length > 1;

    if (this.needSlider) {
      const videoIndex = this.activeGroupData.videos.findIndex(
        (item) => item.id === this.activeVideoId
      );

      this.activeVideoIndex = videoIndex === -1 ? 0 : videoIndex;
    }

    return this.needSlider ? (
      <efb-video-modal-slider
        playVideo={this.playVideo}
        seekTo={this.seekTo}
        muteVideos={this.videoIsMuted}
        sliderContent={this.activeGroupData}
        activeVideoIndex={this.activeVideoIndex}
        videoTitle={this.activeVideoTitle}
      />
    ) : (
      <div class="solo-video">
        <efb-video-modal-item
          playVideo={this.playVideo}
          seekTo={this.seekTo}
          videoIsMuted={this.videoIsMuted}
          trackingId={this.activeGroupData.videos[0].id}
          isActiveItem={true}
          vimeoId={this.activeGroupData.videos[0].vimeoId}
        />
      </div>
    );
  }

  private changeMetaTags(currentVideo: VideoItem, onModalOpening = false) {
    /**
     * change og:description
     */
    const metaDescription = document.createElement('meta');

    metaDescription.setAttribute('property', 'og:description');
    metaDescription.setAttribute('content', currentVideo.metaDescription);

    // Find the existing og:description (if any) and remove it
    const existingMetaDescription = deepQuerySelector('meta[property="og:description"]', document);

    if (!isEmpty(existingMetaDescription)) {
      if (onModalOpening) {
        this.existingMetaDescription = existingMetaDescription.getAttribute('content');
      }

      existingMetaDescription.remove();
    }

    document.head.appendChild(metaDescription);

    /**
     * change og:image
     */
    const ogImageTag = document.createElement('meta');

    ogImageTag.setAttribute('property', 'og:image');
    ogImageTag.setAttribute(
      'content',
      getAssetPath(`/dist/assets/images/sharing/${currentVideo.metaImage}.png`)
    );

    // Find the existing og:image (if any) and remove it
    const existingOGImage = deepQuerySelector('meta[property="og:image"]', document);

    if (!isEmpty(existingOGImage)) {
      if (onModalOpening) {
        this.existingMetaImage = existingOGImage.getAttribute('content');
      }

      existingOGImage.remove();
    }

    document.head.appendChild(ogImageTag);
  }

  /**
   * Lifecycle Hooks
   */

  componentDidLoad() {
    // check if videos need to be muted
    this.videoIsMuted = sessionStorage.getItem('muteVideos') === 'true';

    // match url's hash with requested group & video item
    this.currentActiveVideo = this.activeGroupData.videos[this.activeVideoIndex];
    this.activeVideoTitle = this.currentActiveVideo.title;

    const currentActiveVideoId = this.activeGroupData.videos[this.activeVideoIndex].id;

    window.location.hash = `vgroup=${this.activeVideoGroupId}&vid=${currentActiveVideoId}`;

    // Player's seeker event
    this.videoPlayerSeekerRef.addEventListener('input', async () => {
      const newPlayerPosition =
        (this.currentVideoDuration / 100) * +this.videoPlayerSeekerRef.value;

      this.seekTo = newPlayerPosition;
    });

    // store active item's trackind id
    state.activeVideoTrackingId = currentActiveVideoId;

    // trigger manually page tracking
    pageTracking(
      state.currentLanguage,
      `${this.activeGroupData.dataAnalyticsId}${
        !isEmpty(currentActiveVideoId) ? `-${currentActiveVideoId}` : ''
      }`
    );
    // avoid having tracking being triggered twice for the same video with videoSliderSlideChanged event
    this.modalInitTrackedPageId = this.activeVideoIndex;

    // change meta tags
    setTimeout(() => {
      this.changeMetaTags(this.activeGroupData.videos[this.activeVideoIndex], true);
    }, 300);
  }

  disconnectedCallback() {
    // remove hash from url
    window.history.replaceState(null, null, window.location.href.replace(/#.*$/, ''));

    // put back default meta values

    const metaDescription = deepQuerySelector('meta[property="og:description"]', document);

    metaDescription.setAttribute('content', this.existingMetaDescription);

    const ogImage = deepQuerySelector('meta[property="og:image"]', document);

    ogImage.setAttribute('content', this.existingMetaImage);
  }

  /**
   * Render
   */

  render() {
    const sharingDataAnalyticsValue = !isEmpty(this.activeGroupData)
      ? `b2c-campaign-page-multi-multi-first-timers-${this.activeGroupData.dataAnalyticsId}${
          !isEmpty(state.activeVideoTrackingId) ? `-${state.activeVideoTrackingId}` : ''
        }_share-button_share-menu`
      : '';

    return (
      <Host>
        <efb-base-modal
          ref={(el) => {
            this.modalRef = el;
          }}
          modalId="video-modal"
          transitionType="slide-up"
          modalCanBeClosed={!this.isSharingModalOpen}
        >
          <div
            class={`video-modal ${!this.needSlider ? 'has-one-video' : ''}`}
            role="dialog"
            aria-label="Video modal"
          >
            {/* Ethias logo */}
            {!this.isMobile && (
              <img
                class="ethias-logo"
                src={getAssetPath('/dist/assets/images/logos/ethias-logo.png')}
                srcset={`${getAssetPath('/dist/assets/images/logos/ethias-logo@2x.png')} 2x`}
                alt="Ethias logo"
                width="122"
                height="78"
              />
            )}

            {/* Close button */}
            {!this.isMobile && (
              <div class="desktop-close-button">
                <efb-button
                  text={this.parsedModalData.controls.close}
                  icon="close"
                  compactPlus
                  variant="purple"
                  onEfbButtonClick={() => this.handleCloseClick()}
                />
              </div>
            )}

            <div class="content">
              {/* Header */}
              <header class="header">
                {/* Header buttons */}
                <div class="button-group">
                  {this.isMobile && (
                    <efb-button
                      text={this.parsedModalData.controls.close}
                      icon="back-arrow"
                      compact
                      variant="white"
                      ghost
                      onEfbButtonClick={() => this.handleCloseClick()}
                    />
                  )}
                  <efb-button
                    text={this.parsedModalData.controls.mute}
                    icon={this.videoIsMuted ? 'mute' : 'unmute'}
                    compact
                    variant="white"
                    ghost
                    onEfbButtonClick={() => this.handleMuteClick()}
                  />
                  <efb-button
                    text={this.parsedModalData.controls.share}
                    icon="share"
                    compact
                    variant="white"
                    ghost
                    onEfbButtonClick={() => this.handleShareClick()}
                    dataAnalytics={sharingDataAnalyticsValue}
                  />
                </div>

                {/* Tag */}
                <div class="tag">{this.activeGroupData?.modalGroupTitle}</div>

                {/* External link button */}
                {this.currentActiveVideo?.externalLink && (
                  <efb-button
                    class={`external-link-button ${this.externalLinkIsVisible ? 'is-visible' : ''}`}
                    text={this.currentActiveVideo.externalLink.text}
                    href={this.currentActiveVideo.externalLink.href}
                    target="_blank"
                    variant="purple"
                    icon="forward-arrow"
                    small
                  />
                )}
              </header>

              {/* Videos */}
              {this.renderVideoGroup()}

              {/* Loading Icon */}
              {!this.videoIsReady && <efb-loading-icon part="loading-icon" />}

              {/* Big Play Button */}
              {!this.videoIsPlaying && (
                <efb-button
                  part="big-play-button"
                  icon="play"
                  variant="purple"
                  compact-plus
                  onEfbButtonClick={() => this.clickPlayVideo()}
                />
              )}

              <footer class="footer">
                {/* Player controllers */}
                <div class="player-controllers">
                  {/* Play / Pause */}
                  <efb-button
                    icon={this.videoIsPlaying ? 'pause' : 'play'}
                    variant="white"
                    compact
                    ghost
                    onEfbButtonClick={() => this.clickPlayVideo()}
                  />

                  {/* Seeker */}
                  <div class="player-seeker-wrapper">
                    <input
                      class="player-seeker"
                      ref={(el) => {
                        this.videoPlayerSeekerRef = el;
                      }}
                      type="range"
                      min="0"
                      max="100"
                      value="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </footer>
            </div>

            {/* Sharing modal */}
            {this.isSharingModalOpen && (
              <efb-sharing-modal
                data={this.parsedModalData.sharing}
                dataAnalyticsId={
                  !isEmpty(this.activeGroupData) ? this.activeGroupData.dataAnalyticsId : null
                }
              />
            )}
          </div>
        </efb-base-modal>
      </Host>
    );
  }
}
